import { PieChart } from 'echarts/charts';
import {
  GridComponent,
  LegendComponent,
  TitleComponent,
  TooltipComponent
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import * as echarts from 'echarts/core';
import FalconCardHeader from '@/components/common/FalconCardHeader';
import HelpTooltip from '@/app/common/components/HelpTooltip';
import { useRef } from 'react';
import type { ReactNode } from 'react';
import { Card } from 'react-bootstrap';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import type { EChartsOption } from 'echarts';
import {
  breakpointsMediaQuery,
  getColor,
  useMediaQuery
} from '@/helpers/utils';
import CardChartDropDown from '@/components/common/CardChartDropDown';
import CustomLegent from '@/app/common/components/charts/CustomLegent';
import { useTooltipHide } from '@/hooks/useTooltipHide';

echarts.use([
  TitleComponent,
  TooltipComponent,
  GridComponent,
  PieChart,
  CanvasRenderer,
  LegendComponent
]);

type CircularChartItem = {
  name: string;
  count: number;
  percentage?: number;
  color: string;
};

type CircularChartFormattedItem = {
  name: string;
  value: number;
  color: string;
};

const getOptions = (
  data: CircularChartFormattedItem[],
  isMdUp: boolean
): EChartsOption => ({
  textStyle: {
    fontFamily: 'dana'
  },
  color: data.map(item => getColor(item.color)),
  tooltip: {
    triggerOn: isMdUp ? 'mousemove' : 'click',
    trigger: 'item',
    padding: [7, 10],
    backgroundColor: getColor('gray-100'),
    borderColor: getColor('gray-300'),
    textStyle: { color: getColor('gray-1100') },
    borderWidth: 1,
    transitionDuration: 0,
    formatter: (params: any) =>
      `<div><strong>${params.data.name}:</strong><br/> ${params.data.value} (${params.percent}%)</div>`.toPersianDigit()
  },

  legend: {
    show: false
  },
  grid: { right: '0%', left: '0%', bottom: '0%', top: '0%' },
  series: [
    {
      type: 'pie',
      radius: ['60%', '40%'],
      avoidLabelOverlap: false,
      itemStyle: {
        borderWidth: 2,
        borderColor: getColor('gray-100')
      },
      label: {
        show: false
      },
      emphasis: {
        scale: false
      },
      top: '-20%',
      left: '-20%',
      right: '-20%',
      bottom: '-20%',
      data
    }
  ]
}) as EChartsOption;

interface CircularChartCardProps {
  data: CircularChartItem[];
  title: ReactNode;
  tooltip?: string;
  footer?: ReactNode;
  className?: string;
}

const CircularChartCard = ({
  data,
  title,
  tooltip,
  footer,
  className
}: CircularChartCardProps) => {
  const chartRef = useRef<ReactEChartsCore | null>(null);
  const isMdUp = useMediaQuery(breakpointsMediaQuery.md);
  useTooltipHide(chartRef);

  const cardRef = useRef<HTMLDivElement | null>(null);
  const formattedData = data.map(item => ({
    name: item.name,
    value: item.count,
    color: item.color
  }));
  return (
    <Card ref={cardRef} className={className}>
      <FalconCardHeader
        title={
          <div>
            {title}
            {tooltip ? <HelpTooltip html={tooltip} /> : null}
          </div>
        }
        titleTag="h6"
        className="py-2"
        light
        endEl={<CardChartDropDown cardRef={cardRef} chartRef={chartRef} />}
      />
      <Card.Body className="px-0">
        <ReactEChartsCore
          ref={chartRef}
          echarts={echarts}
          option={getOptions(formattedData, isMdUp)}
          style={{ height: 250 }}
        />
      </Card.Body>

      <div className="d-flex flex-wrap mt-n4 mb-3 gap-2 justify-content-center pt-1 align-items-center">
        {data.map(item => (
          <CustomLegent
            key={item.name}
            name={item.name}
            chartRef={chartRef}
            color={getColor(item.color)}
          />
        ))}
      </div>
      {footer != null ? (
        <Card.Footer className="bg-body-tertiary fs--1 text-center">
          {footer}
        </Card.Footer>
      ) : null}
    </Card>
  );
};

export default CircularChartCard;
